"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, User, Menu, X, ChevronRight, LogOut, Heart, Truck } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSession, signOut } from "next-auth/react";
import { useCartStore } from "@/lib/store/useCartStore";
import { useWishlistStore } from "@/lib/store/useWishlistStore";
import NotificationBell from "./NotificationBell";

type NavLink = { name: string; href: string };

const Navbar = ({ settings }: { settings: any }) => {
  const { data: session, status } = useSession();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const totalItems = useCartStore((state) => state.totalItems());
  const wishlistItems = useWishlistStore((state) => state.items.length);
  const syncFromServer = useWishlistStore((state) => state.syncFromServer);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    if (status === "authenticated") {
      syncFromServer();
    }
  }, [status]);

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);

    // Kullanıcının isteği: Geri dönüşlerde (bfcache) sayfayı yenile
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  const navLinks: NavLink[] = settings?.navLinks || [
    { name: "Parfüm Ürünlerimiz", href: "/products" },
    { name: "Hakkımızda", href: "/about" },
    { name: "İletişim", href: "/contact" },
  ];

  return (
    <>
      <nav
        className={cn(
          "fixed top-0 left-0 right-0 z-[100] transition-all duration-500 px-4 sm:px-6 md:px-12 py-3 sm:py-4",
          isScrolled
            ? "bg-[#09090B]/85 backdrop-blur-[20px] border-b border-[rgba(201,168,76,0.15)] py-3"
            : "bg-transparent border-b border-transparent"
        )}
      >
        <div className="max-w-[1440px] mx-auto flex items-center justify-between">
          {/* LOGO */}
          <Link href="/" className="group relative z-10 flex items-center gap-2 sm:gap-3">
            <Image src="/logo.png" alt="Parfume" width={75} height={75} className="object-contain w-12 h-12 sm:w-[75px] sm:h-[75px]" priority />
            <span className="font-serif text-[15px] sm:text-lg tracking-[0.15em] text-[#C9A84C] font-light hidden sm:block">
              Parfume
            </span>
          </Link>

          {/* DESKTOP NAV LINKS */}
          <ul className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              const isActive = pathname === link.href;
              return (
                <li key={link.name} className="relative py-1">
                  <Link
                    href={link.href}
                    className={cn(
                      "text-[0.75rem] uppercase tracking-[0.12em] font-medium transition-colors duration-300",
                      isActive ? "text-[#C9A84C]" : "text-[#7A7670] hover:text-[#C9A84C]"
                    )}
                  >
                    {link.name}
                  </Link>
                  {isActive && (
                    <motion.div
                      layoutId="nav-underline"
                      className="absolute bottom-0 left-0 right-0 h-[1px] bg-[#C9A84C]"
                    />
                  )}
                </li>
              );
            })}
          </ul>

          {/* RIGHT SIDE ELEMENTS */}
          <div className="flex items-center gap-4 sm:gap-6 shrink-0">
            {/* Session Block - Moved out of hidden/flex container to ensure visibility */}
            <div className="flex items-center gap-4 sm:gap-6">
              {/* Kargo Takip Icon */}
              <Link href="/order-tracking" className="relative hidden md:flex items-center gap-2 text-[#7A7670] hover:text-[#C9A84C] transition-colors group" title="Kargo Takip">
                <Truck size={20} strokeWidth={1.5} />
                <span className="text-[10px] uppercase tracking-widest hidden lg:inline group-hover:text-[#C9A84C]">Kargo Takip</span>
              </Link>
              
              <div className="hidden md:flex items-center gap-4 min-w-[120px] justify-end h-10">
                {status === "loading" ? (
                  <div className="w-20 h-4 bg-white/5 animate-pulse rounded-full" />
                ) : session ? (
                  <div className="flex items-center gap-4">
                    <Link href="/profil" className="text-[#7A7670] hover:text-[#C9A84C] transition-colors flex items-center gap-2 group">
                      <User size={20} strokeWidth={1.5} />
                      <span className="text-[10px] uppercase tracking-widest hidden lg:inline group-hover:text-[#C9A84C]">Profilim</span>
                    </Link>
                    <button 
                      onClick={() => signOut({ callbackUrl: "/" })}
                      className="text-[#7A7670] hover:text-[#E24B4A] transition-colors"
                    >
                      <LogOut size={18} strokeWidth={1.5} />
                    </button>
                  </div>
                ) : (
                  <Link href="/giris" className="text-[#7A7670] hover:text-[#C9A84C] transition-colors flex items-center gap-2 group">
                    <User size={20} strokeWidth={1.5} />
                    <span className="text-[10px] uppercase tracking-widest hidden lg:inline group-hover:text-[#C9A84C]">Giriş / Kayıt</span>
                  </Link>
                )}
              </div>

              {/* Bildirimler */}
              <NotificationBell />

              {/* Wishlist Icon */}
              <Link href="/favoriler" className="relative group">
                <Heart
                  size={22}
                  strokeWidth={1.5}
                  className="text-[#7A7670] group-hover:text-[#C9A84C] transition-colors"
                />
                {mounted && wishlistItems > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-[#C9A84C] text-black text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {wishlistItems}
                  </span>
                )}
              </Link>

              {/* Cart Icon */}
              <Link href="/cart" className="relative group">
                <ShoppingBag
                  size={22}
                  strokeWidth={1.5}
                  className="text-[#7A7670] group-hover:text-[#C9A84C] transition-colors"
                />
                {mounted && totalItems > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-[#C9A84C] text-black text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </Link>
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-white flex flex-col gap-1.5"
            >
              <motion.span
                animate={isMenuOpen ? { rotate: 45, y: 7.5 } : { rotate: 0, y: 0 }}
                className="w-6 h-[1.5px] bg-white block"
              />
              <motion.span
                animate={isMenuOpen ? { opacity: 0 } : { opacity: 1 }}
                className="w-6 h-[1.5px] bg-white block"
              />
              <motion.span
                animate={isMenuOpen ? { rotate: -45, y: -7.5 } : { rotate: 0, y: 0 }}
                className="w-6 h-[1.5px] bg-white block"
              />
            </button>
          </div>
        </div>
      </nav>

      {/* MOBILE DRAWER */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] md:hidden"
            />
            <motion.div
              initial={{ x: 300 }}
              animate={{ x: 0 }}
              exit={{ x: 300 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 h-full w-[280px] bg-[#111114] border-l border-[rgba(201,168,76,0.15)] z-[120] p-8 flex flex-col md:hidden"
            >
              <div className="flex justify-end mb-12">
                <button onClick={() => setIsMenuOpen(false)} className="text-[#7A7670] hover:text-white">
                  <X size={24} strokeWidth={1.5} />
                </button>
              </div>

              <div className="flex flex-col gap-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    href={link.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="text-[0.9rem] uppercase tracking-[0.2em] text-[#F4F0E8] hover:text-[#C9A84C] transition-colors flex items-center justify-between group"
                  >
                    {link.name}
                    <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                ))}
              </div>

              <div className="mt-auto pt-12 border-t border-white/5 space-y-8">
                  {status === "loading" ? (
                    <div className="w-full h-8 bg-white/5 animate-pulse rounded-lg" />
                  ) : session ? (
                  <div className="flex flex-col gap-4">
                    <Link
                      href="/profil"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 text-[0.8rem] uppercase tracking-widest text-[#F4F0E8]"
                    >
                      <User size={18} strokeWidth={1.5} />
                      Profilim
                    </Link>
                    <button
                      onClick={() => signOut({ callbackUrl: "/" })}
                      className="flex items-center gap-3 text-[0.8rem] uppercase tracking-widest text-[#E24B4A]"
                    >
                      <LogOut size={18} strokeWidth={1.5} />
                      Çıkış Yap
                    </button>
                  </div>
                ) : (
                  <Link
                    href="/giris"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 text-[0.8rem] uppercase tracking-widest text-[#F4F0E8]"
                  >
                    <User size={18} strokeWidth={1.5} />
                    Giriş Yap / Kayıt Ol
                  </Link>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
